import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";

// Import components
import NotificationTable from "./components/NotificationTable";
import NotificationModal from "./components/NotificationModal";
import BulkActionsBar from "./components/BulkActionsBar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getNotificationsInfoService } from "redux/services/general_service";
import { toast } from "react-toastify";

const NotificationsManagment = () => {
  const { t } = useTranslation();

  const { generalNotifications } = useSelector((state) => state.general);

  const dispatch = useDispatch();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [signals, setSignals] = useState([]);
  const [filteredSignals, setFilteredSignals] = useState([]);
  const [selectedSignals, setSelectedSignals] = useState([]);
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [editingSignal, setEditingSignal] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch notifications from store/API
  useEffect(() => {
    dispatch(getNotificationsInfoService({ page: 1 }));
    setSignals(generalNotifications);
    setFilteredSignals(generalNotifications);
  }, [dispatch, generalNotifications]);

  // Filter signals based on search and filters
  const applyFilters = (filters = {}) => {
    let filtered = [...signals];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (signal) =>
          signal?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          signal?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Apply status filter
    if (filters?.status) {
      filtered = filtered.filter((signal) => signal?.status === filters?.status);
    }

    // Apply signal type filter
    if (filters?.signalType) {
      filtered = filtered.filter((signal) => signal?.type === filters?.signalType);
    }

    // Apply budget range filter
    if (filters?.budgetRange?.min) {
      filtered = filtered.filter(
        (signal) => signal?.budget >= parseFloat(filters?.budgetRange?.min)
      );
    }
    if (filters?.budgetRange?.max) {
      filtered = filtered.filter(
        (signal) => signal?.budget <= parseFloat(filters?.budgetRange?.max)
      );
    }

    // Apply date range filter
    if (filters?.dateRange?.start) {
      filtered = filtered.filter(
        (signal) => new Date(signal.startDate) >= new Date(filters.dateRange.start)
      );
    }
    if (filters?.dateRange?.end) {
      filtered = filtered.filter(
        (signal) => new Date(signal.endDate) <= new Date(filters.dateRange.end)
      );
    }

    // Apply target audience filter
    if (filters?.targetAudience?.length > 0) {
      filtered = filtered.filter((signal) =>
        filters?.targetAudience?.some((audience) =>
          signal?.targetAudience?.includes(audience)
        )
      );
    }

    // Apply search term filter from filters
    if (filters?.searchTerm) {
      filtered = filtered.filter(
        (signal) =>
          signal?.name?.toLowerCase()?.includes(filters?.searchTerm?.toLowerCase()) ||
          signal?.description?.toLowerCase()?.includes(filters?.searchTerm?.toLowerCase())
      );
    }

    setFilteredSignals(filtered);
  };

  // Sort signals
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig?.key === key && sortConfig?.direction === "asc") {
      direction = "desc";
    }

    const sortedSignals = [...filteredSignals].sort((a, b) => {
      if (a?.[key] < b?.[key]) return direction === "asc" ? -1 : 1;
      if (a?.[key] > b?.[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredSignals(sortedSignals);
    setSortConfig({ key, direction });
  };

  // Handle signal actions
  const handleEditSignal = (signal) => {
    setEditingSignal(signal);
    setShowSignalModal(true);
  };

  const handlePauseResumeSignal = (signal) => {
    const newStatus = signal?.status === "active" ? "paused" : "active";
    const updatedSignals = signals.map((s) =>
      s?.id === signal?.id ? { ...s, status: newStatus } : s
    );
    setSignals(updatedSignals);
    applyFilters({});
  };

  const handleDuplicateSignal = (signal) => {
    const duplicatedSignal = {
      ...signal,
      id: Date.now(),
      name: `${signal?.name} (Copy)`,
      status: "draft",
      spent: 0,
      impressions: 0,
      clicks: 0,
      ctr: 0,
      roi: 0,
    };
    setSignals([...signals, duplicatedSignal]);
    applyFilters({});
  };

  const handleDeleteSignal = (signalId) => {
    if (window.confirm(`هل أنت متأكد أنك تريد حذف هذا الإشعار؟`)) {
      // لو عندك API للحذف استبدل السطرين التاليين بالـ dispatch
      const updatedSignals = signals.filter((s) => s.id !== signalId);
      setSignals(updatedSignals);
      applyFilters({});
      toast.success("تم الحذف بنجاح!");
    }
  };

  const handleSaveSignal = (signalData) => {
    if (editingSignal) {
      // Update existing signal
      const updatedSignals = signals.map((s) =>
        s?.id === editingSignal?.id ? { ...s, ...signalData } : s
      );
      setSignals(updatedSignals);
    } else {
      // Create new signal
      const newSignal = {
        ...signalData,
        id: Date.now(),
        spent: 0,
        impressions: 0,
        clicks: 0,
        ctr: 0,
        roi: 0,
        status: "draft",
        mediaUrl:
          signalData?.mediaFiles?.[0]?.url ||
          "https://images.pexels.com/photos/267350/pexels-photo-267350.jpeg",
      };
      setSignals([...signals, newSignal]);
    }
    setEditingSignal(null);
    applyFilters({});
  };

  // Apply search filter when search term changes
  useEffect(() => {
    applyFilters({});
  }, [searchTerm, signals]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <>
      <Helmet>
        <title>Signal Management - Business Manager Pro</title>
        <meta
          name="description"
          content="Manage and optimize your signal systems with comprehensive analytics and performance tracking."
        />
      </Helmet>

      <div className="p-6">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t("notifications")}
            </h1>
          </div>

          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <Button
              onClick={() => {
                setEditingSignal(null);
                setShowSignalModal(true);
              }}
              iconName="Plus"
              iconPosition="left"
            >
              إرسال إشعار
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <NotificationTable
              notifications={filteredSignals}
              selectedSignals={selectedSignals}
              onSelectionChange={setSelectedSignals}
              onEdit={handleEditSignal}
              onPauseResume={handlePauseResumeSignal}
              onDuplicate={handleDuplicateSignal}
              onDelete={handleDeleteSignal} // ✅ زر الحذف
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </div>
        </div>
      </div>

      {/* Signal Modal */}
      <NotificationModal
        isOpen={showSignalModal}
        onClose={() => {
          setShowSignalModal(false);
          setEditingSignal(null);
        }}
        signal={editingSignal}
        onSave={handleSaveSignal}
      />
    </>
  );
};

export default NotificationsManagment;
